import React, { type FC, useEffect, useState, useContext } from 'react';

import { BREAKPOINT, getColor } from '@lucid/theme';
import { isBrowser } from '@lucid/utils';
import { useSmartlingStrings } from '@lucid/hooks';
import kebabCase from 'lodash/kebabCase';
import throttle from 'lodash/throttle';

import Heading from '../heading/heading';
import Text from '../text/text';
import Paper from '../paper/paper';
import Link from '../link/link';
import Box from '../box/box';
import LayoutContext from '../context/layout-context';
import type { Lucid } from '../../types/types';

export interface LinkBankProps {
  /** The heading for the Link Bank component */
  heading?: string;
  /** Text to be displayed below the heading on the Link Bank component */
  text?: string;
  /** The data for the competitor page links */
  competitors: {
    label: string;
    value: string;
  }[];
  /** The color of the divider between the headings and the link section */
  dividerColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
}

const LinkBank: FC<LinkBankProps> = ({ heading, text, competitors, dividerColor }) => {
  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_competitor_compare'],
    shortCode,
  });

  // Calculate rows for link grid to render vertically
  const [numColumns, setNumColumns] = useState(4);

  const handleResize = throttle(() => {
    if (isBrowser()) {
      const windowWidth = window.innerWidth;
      setNumColumns(windowWidth < parseInt(BREAKPOINT.BP_500) ? 1 : windowWidth < parseInt(BREAKPOINT.BP_1024) ? 2 : 4);
    }
  });

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  const renderCompetitorLinks = (columns: number) => {
    const numRows = Math.ceil(competitors?.length / columns);
    const chunkedCompetitors = competitors
      ?.map((_, i) => (i % numRows === 0 ? competitors.slice(i, i + numRows) : null))
      .filter(Boolean);

    return chunkedCompetitors?.map((chunk, index) => (
      <Box
        key={`competitor-${kebabCase(competitors[index]?.value)}`}
        css={theme => ({ display: 'flex', flexDirection: 'column', gap: theme.space.s16 })}
      >
        {chunk?.map((competitor, chunkIndex) => (
          <Link
            key={kebabCase(`${competitor.value}-${index}-${chunkIndex}`)}
            href={competitor.value}
            underline="always"
            variant="primaryAlternate"
          >
            {competitor.label}
          </Link>
        ))}
      </Box>
    ));
  };

  return (
    <Paper
      css={theme => ({
        label: 'linkBank',
        border: `1px solid ${theme.colors.coolGrey[100]}`,
        boxShadow: `10px 10px 0 0 ${theme.colors.coolGrey[10]}`,
      })}
      variant="raisedLow"
    >
      <Box
        css={theme => ({
          maxWidth: '780px',
          textAlign: 'center',
          margin: `0 auto ${theme.space.s40}px`,
        })}
      >
        <Heading as="h2" variant="heading2XL">
          {heading || withSmartling('mktg_competitor_compare')}
        </Heading>
        {text && <Text variant="paragraphM400">{text}</Text>}
      </Box>
      <Box
        css={theme => ({
          display: 'grid',
          gridTemplateColumns: '1fr',
          gridRowGap: theme.space.s16,
          gridColumnGap: theme.space.s24,
          position: 'relative',
          paddingTop: theme.space.s40,
          paddingLeft: theme.space.s40,
          '&::before': {
            position: 'absolute',
            content: '""',
            width: 'calc(100% + 80px)',
            height: '4px',
            background:
              getColor({
                color: dividerColor,
                theme,
              }) || theme.colors.coolGrey[100],
            top: 0,
            left: '-40px',
            boxShadow: `0 1px 0 0 ${theme.colors.coolGrey[10]}`,
          },
          [theme.breakpoints.bp500.min]: {
            gridTemplateColumns: 'repeat(2, 1fr)',
            paddingLeft: 0,
            justifyItems: 'center',
          },
          [theme.breakpoints.bp1024.min]: {
            gridTemplateColumns: 'repeat(4, 1fr)',
          },
        })}
      >
        {renderCompetitorLinks(numColumns)}
      </Box>
    </Paper>
  );
};

export default LinkBank;

export { LinkBank };
